import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { LogOut, User, Lock, Bell, Shield, Trash2, FileText, UserX, Search, X, MessageCircle, Globe, Film, ChevronRight, Sun, Moon, Phone, MapPin, Mail, ExternalLink, BarChart3, Wifi, RefreshCw, Smartphone, Flag } from 'lucide-react';
import { ReportAIContentModal, aiReportTexts } from '../components/ReportAIContent';
import Navigation from '../components/Navigation';
import AdDisplay from '../components/AdDisplay';
import MyEsimsPanel from '../components/MyEsimsPanel';
import MyRefundsPanel from '../components/MyRefundsPanel';
import EmailVerificationCard from '../components/EmailVerificationCard';
import EmailChangeCard from '../components/EmailChangeCard';
import MyApplicationsCard from '../components/MyApplicationsCard';
import ConnectedDevicesPanel from '../components/ConnectedDevicesPanel';
import { SubscriptionButton, SubscriptionModal } from '../components/myia/SubscriptionPanel';
import LinkedAccountsPanel from '../components/LinkedAccountsPanel';
import PromoteProfileSection from '../components/PromoteProfileSection';
import ReferralSection from '../components/ReferralSection';
import { toast } from 'sonner';
import axios from 'axios';
import { getMediaUrl } from '../utils/mediaUrl';
import { isNativePlatform, getPlatform } from '../capacitor-utils';
import { PushNotifications } from '@capacitor/push-notifications';
import CallUsageChart, { formatSeconds } from '../components/CallUsageChart';


// v60.5 — Stories privées (13 langues)
const PRIVATE_STORIES_I18N = {
  fr: { title: 'Stories privées', sub: 'Seuls vos abonnés voient vos stories.', on: 'Stories privées activées', off: 'Stories visibles par tous' },
  en: { title: 'Private stories', sub: 'Only your followers can see your stories.', on: 'Private stories enabled', off: 'Stories visible to everyone' },
  es: { title: 'Stories privadas', sub: 'Solo tus seguidores ven tus stories.', on: 'Stories privadas activadas', off: 'Stories visibles para todos' },
  pt: { title: 'Stories privadas', sub: 'Só os teus seguidores veem as tuas stories.', on: 'Stories privadas ativadas', off: 'Stories visíveis para todos' },
  de: { title: 'Private Stories', sub: 'Nur deine Follower sehen deine Stories.', on: 'Private Stories aktiviert', off: 'Stories für alle sichtbar' },
  it: { title: 'Storie private', sub: 'Solo i tuoi follower vedono le tue storie.', on: 'Storie private attivate', off: 'Storie visibili a tutti' },
  nl: { title: 'Privéstories', sub: 'Alleen je volgers zien je stories.', on: 'Privéstories ingeschakeld', off: 'Stories zichtbaar voor iedereen' },
  ar: { title: 'قصص خاصة', sub: 'فقط متابعوك يرون قصصك.', on: 'تم تفعيل القصص الخاصة', off: 'القصص مرئية للجميع' },
  ru: { title: 'Приватные истории', sub: 'Только ваши подписчики видят ваши истории.', on: 'Приватные истории включены', off: 'Истории видны всем' },
  zh: { title: '私密动态', sub: '只有关注者能看到你的动态.', on: '已开启私密动态', off: '动态对所有人可见' },
  ja: { title: '非公開ストーリー', sub: 'フォロワーだけがストーリーを見られます.', on: '非公開ストーリーを有効化', off: 'ストーリーは全員に公開' },
  hi: { title: 'निजी स्टोरीज़', sub: 'केवल आपके फ़ॉलोअर आपकी स्टोरीज़ देखें।', on: 'निजी स्टोरीज़ चालू', off: 'स्टोरीज़ सभी को दिखें' },
  sw: { title: 'Hadithi za faragha', sub: 'Wafuasi wako pekee huona hadithi zako.', on: 'Hadithi za faragha zimewashwa', off: 'Hadithi zinaonekana kwa wote' },
  ht: { title: 'Story prive', sub: 'Se sèlman abòne ou yo ki wè story ou yo.', on: 'Story prive aktive', off: 'Story vizib pou tout moun' },
};



const Settings = () => {
  const { user, logout, refreshUser } = useAuth();
  const { language, setLanguage, t, getLanguageName, getFlag, supportedLanguages } = useLanguage();
  const { theme, setTheme, isDark } = useTheme();
  const navigate = useNavigate();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showAIReport, setShowAIReport] = useState(false);
  const [confirmDob, setConfirmDob] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [showSubModal, setShowSubModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showChangeEmailModal, setShowChangeEmailModal] = useState(false);
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [loadingBlocked, setLoadingBlocked] = useState(true);
  const [notificationStatus, setNotificationStatus] = useState('unknown'); // 'unknown', 'granted', 'denied', 'prompt'
  const [requestingNotifications, setRequestingNotifications] = useState(false);
  const [weeklyCinemaEnabled, setWeeklyCinemaEnabled] = useState(true);
  const [locationStatus, setLocationStatus] = useState('unknown'); // 'granted', 'denied', 'prompt', 'unknown', 'web'
  const [callUsage, setCallUsage] = useState(null); // {series, total_seconds, total_calls}

  // Load 30-day call usage for the current user
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get('/api/calls/usage?days=30');
        if (alive) setCallUsage(res.data);
      } catch (_) { /* silent */ }
    })();
    return () => { alive = false; };
  }, []);

  // Check notification permission status on mount
  useEffect(() => {
    checkNotificationPermission();
    checkLocationPermission();
  }, []);

  // Load weekly cinema digest preference
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get('/api/users/me/notification-prefs');
        const enabled = res.data?.email_prefs?.weekly_cinema_digest;
        setWeeklyCinemaEnabled(enabled !== false); // default true
      } catch (_) { /* silent */ }
    })();
  }, []);

  const checkLocationPermission = async () => {
    if (!isNativePlatform()) {
      // On web, use the Permissions API
      if (navigator.permissions) {
        try {
          const perm = await navigator.permissions.query({ name: 'geolocation' });
          setLocationStatus(perm.state); // 'granted', 'denied', 'prompt'
          perm.onchange = () => setLocationStatus(perm.state);
        } catch {
          setLocationStatus('unknown');
        }
      } else {
        setLocationStatus('unknown');
      }
      return;
    }
    // On native: use Capacitor Geolocation plugin to CHECK permission only
    // (no prompt). Falls back to navigator if plugin unavailable.
    try {
      const { Geolocation } = await import('@capacitor/geolocation');
      const perm = await Geolocation.checkPermissions();
      const st = perm.location || perm.coarseLocation;
      if (st === 'granted') setLocationStatus('granted');
      else if (st === 'denied') setLocationStatus('denied');
      else setLocationStatus('prompt');
    } catch (_) {
      setLocationStatus('unknown');
    }
  };

  const handleOpenLocationSettings = () => {
    if (isNativePlatform()) {
      // On iOS/Android, open the app's system settings
      try {
        // Capacitor way to open app settings
        import('@capacitor/app').then(({ App }) => {
          // openUrl to the app settings deep link
          if (getPlatform() === 'ios') {
            App.openUrl({ url: 'app-settings:' });
          } else {
            App.openUrl({ url: 'package:' + 'com.destinestudio.app' });
          }
        }).catch(() => {
          toast.info('Ouvrez Réglages > Destine > Position');
        });
      } catch {
        toast.info('Ouvrez Réglages > Destine > Position');
      }
    } else {
      toast.info('Ouvrez les paramètres de votre navigateur pour gérer la localisation');
    }
  };

  const checkNotificationPermission = async () => {
    if (!isNativePlatform()) {
      setNotificationStatus('web');
      return;
    }
    
    try {
      const permStatus = await PushNotifications.checkPermissions();
      setNotificationStatus(permStatus.receive);
    } catch (error) {
      console.error('Error checking notification permission:', error);
      setNotificationStatus('unknown');
    }
  };

  const requestNotificationPermission = async () => {
    if (!isNativePlatform()) {
      toast.info('Les notifications push sont disponibles uniquement sur l\'app mobile');
      return;
    }

    // If already granted, open the device settings so user can toggle off/on
    if (notificationStatus === 'granted' || notificationStatus === 'denied') {
      try {
        // On iOS, NativeSettings or App.openSettings(); fallback Browser.open with iOS prefs URL
        const { App } = await import('@capacitor/app');
        if (App?.openSettings) {
          await App.openSettings();
          return;
        }
      } catch (e) { /* ignore */ }
      try {
        const { Browser } = await import('@capacitor/browser');
        await Browser.open({ url: 'app-settings:' });
        return;
      } catch (e) {
        toast.info('Ouvrez les Réglages iPhone → Destine → Notifications');
        return;
      }
    }

    setRequestingNotifications(true);
    
    try {
      // Request permission
      let permStatus = await PushNotifications.checkPermissions();
      
      if (permStatus.receive === 'prompt') {
        permStatus = await PushNotifications.requestPermissions();
      }
      
      if (permStatus.receive === 'granted') {
        // Register for push notifications
        await PushNotifications.register();
        
        // Listen for registration success
        PushNotifications.addListener('registration', async (token) => {
          console.log('📱 Push token:', token.value?.substring(0, 20) + '...');
          
          // Send token to backend
          try {
            await axios.post('/api/push/register-device', {
              device_token: token.value,
              platform: getPlatform()
            });
            toast.success('Notifications activées !');
          } catch (error) {
            console.error('Error registering device:', error);
          }
        });
        
        setNotificationStatus('granted');
        toast.success('Notifications activées !');
      } else if (permStatus.receive === 'denied') {
        setNotificationStatus('denied');
        toast.error('Permission refusée. Activez les notifications dans les réglages de votre iPhone.');
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      toast.error('Erreur lors de l\'activation des notifications');
    } finally {
      setRequestingNotifications(false);
    }
  };

  // Load blocked users on mount
  useEffect(() => {
    loadBlockedUsers();
    loadDndStatus();
  }, []);

  const [dndEnabled, setDndEnabled] = useState(false);
  const loadDndStatus = async () => {
    try {
      const res = await axios.get('/api/privacy/dnd');
      setDndEnabled(!!res.data?.enabled);
    } catch (e) {}
  };
  const toggleDnd = async () => {
    try {
      const next = !dndEnabled;
      await axios.put('/api/privacy/dnd', { enabled: next });
      setDndEnabled(next);
      toast.success(next ? (t('calls.dnd_title') || 'Ne pas déranger') + ' ✓' : '✓');
    } catch (e) {
      toast.error('Erreur');
    }
  };

  // Search users debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.length >= 2) {
        searchUsers();
      } else {
        setSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const loadBlockedUsers = async () => {
    try {
      const response = await axios.get('/api/blocked-users');
      setBlockedUsers(response.data);
    } catch (error) {
      console.error('Error loading blocked users:', error);
    } finally {
      setLoadingBlocked(false);
    }
  };

  const searchUsers = async () => {
    setSearching(true);
    try {
      const response = await axios.get(`/api/blocked-users/search?query=${encodeURIComponent(searchQuery)}`);
      setSearchResults(response.data);
    } catch (error) {
      console.error('Error searching users:', error);
    } finally {
      setSearching(false);
    }
  };

  const handleBlockUser = async (userId, userName) => {
    try {
      await axios.post(`/api/users/${userId}/block`);
      toast.success(`${userName} a été bloqué. Vous ne verrez plus son contenu.`);
      setSearchQuery('');
      setSearchResults([]);
      loadBlockedUsers();
    } catch (error) {
      console.error('Error blocking user:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors du blocage');
    }
  };

  const handleUnblockUser = async (userId, userName) => {
    try {
      await axios.post(`/api/users/${userId}/unblock`);
      toast.success(`${userName} a été débloqué`);
      loadBlockedUsers();
    } catch (error) {
      console.error('Error unblocking user:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors du déblocage');
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Déconnexion réussie');
    navigate('/');
  };

  const handleDeleteAccount = async () => {
    if (!confirmDob) {
      toast.error('Veuillez saisir votre date de naissance');
      return;
    }
    setDeleting(true);
    try {
      await axios.delete('/api/users/me', { data: { date_of_birth: confirmDob } });
      toast.success('Compte supprimé');
      logout();
      navigate('/');
    } catch (error) {
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      if (status === 403 && detail) {
        toast.error(detail);
      } else if (status === 400 && detail) {
        toast.error(detail);
      } else {
        toast.error('Erreur lors de la suppression');
      }
      console.error('Error deleting account:', error);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className={`min-h-screen text-white pb-20 ${isDark ? 'bg-black' : ''}`} style={!isDark ? { background: 'var(--theme-bg)' } : {}}>
      <div className="container mx-auto px-4 py-6 max-w-2xl">
        <button
          onClick={() => navigate('/profile')}
          className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors mb-6"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span>{t('settings.backToProfile', 'Retour au profil')}</span>
        </button>
        
        <h1 className="text-4xl font-bold mb-2 bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
          {t('settings.titleHero')}
        </h1>
        <p className="text-gray-400 mb-8">{t('settings.subtitleHero')}</p>

        {/* Ad Display */}
        <div className="mb-6">
          <AdDisplay zone="settings" />
        </div>

        <div className="space-y-4">
          {/* Account Info */}
          <div className="bg-white/5 rounded-2xl border border-white/10 p-6">
            <div className="flex items-center space-x-3 mb-4">
              <User className="w-6 h-6 text-blue-400" />
              <h2 className="text-xl font-semibold">{t('settings.accountInfo')}</h2>
            </div>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between items-center">
                <span className="text-gray-400">{t('auth.email')}</span>
                <div className="flex items-center gap-2">
                  <span className="font-medium">{user?.email}</span>
                  <button 
                    onClick={() => setShowChangeEmailModal(true)}
                    className="p-1.5 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
                    data-testid="change-email-btn"
                    title="Modifier l'email"
                  >
                    <Mail className="w-3.5 h-3.5 text-blue-400" />
                  </button>
                </div>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Nom</span>
                <span className="font-medium">{user?.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Type de compte</span>
                <span className="font-medium">{user?.account_type}</span>
              </div>
              {user?.age && (
                <div className="flex justify-between">
                  <span className="text-gray-400">{t('auth.age')}</span>
                  <span className="font-medium">{user.age} ans</span>
                </div>
              )}
              {user?.account_type === 'PRO' && user?.siret && (
                <div className="flex justify-between">
                  <span className="text-gray-400">{t('auth.siret')}</span>
                  <span className="font-medium">{user.siret}</span>
                </div>
              )}
              {user?.is_admin && (
                <div className="flex justify-between">
                  <span className="text-gray-400">Statut</span>
                  <span className="px-3 py-1 bg-magenta-500/20 text-magenta-400 rounded-full text-xs font-semibold">ADMIN</span>
                </div>
              )}
            </div>
            {/* Email verification card */}
            <div className="mt-4">
              <EmailVerificationCard />
            </div>
            {/* Change email card */}
            <div className="mt-4">
              <EmailChangeCard currentEmail={user?.email} onChanged={() => window.location.reload()} />
            </div>
            {/* v49.5 — My MyJob applications */}
            <div className="mt-4">
              <MyApplicationsCard />
            </div>
          </div>

          {/* Language Selection */}
          <div className="bg-white/5 rounded-2xl border border-white/10 p-6">
            <div className="flex items-center space-x-3 mb-4">
              <Globe className="w-6 h-6 text-cyan-400" />
              <h2 className="text-xl font-semibold">{t('settings.language')}</h2>
            </div>
            <button 
              onClick={() => setShowLanguageModal(true)}
              className="w-full text-left p-3 bg-white/5 hover:bg-white/10 rounded-xl transition-colors flex items-center justify-between"
            >
              <div>
                <div className="font-medium mb-1">{t('settings.selectLanguage')}</div>
                <div className="text-sm text-gray-400 flex items-center gap-2">
                  <span>{getFlag(language)}</span>
                  <span>{getLanguageName(language)}</span>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          {/* Theme Selection */}
          <div className="bg-white/5 rounded-2xl border border-white/10 p-6">
            <div className="flex items-center space-x-3 mb-4">
              {isDark ? <Moon className="w-6 h-6 text-violet-400" /> : <Sun className="w-6 h-6 text-yellow-400" />}
              <h2 className="text-xl font-semibold">{t('settings.appearance')}</h2>
            </div>
            <div className="grid grid-cols-3 gap-3" data-testid="theme-picker">
              <button
                onClick={() => setTheme('dark')}
                className={`p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                  theme === 'dark' 
                    ? 'border-violet-500 bg-violet-500/20' 
                    : 'border-white/10 bg-white/5 hover:bg-white/10'
                }`}
                data-testid="theme-option-dark"
              >
                <div className="w-full h-14 rounded-lg bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center border border-white/10" data-keep-dark="" style={{ background: 'linear-gradient(to bottom right, #111827, #000, #111827)' }}>
                  <Moon className="w-6 h-6 text-violet-400" />
                </div>
                <span className="font-medium text-xs text-center">{t('settings.darkMode')}</span>
                {theme === 'dark' && <span className="text-[10px] text-violet-400">{t('settings.active')}</span>}
              </button>
              <button
                onClick={() => setTheme('light')}
                className={`p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                  theme === 'light' 
                    ? 'border-pink-500 bg-pink-500/20' 
                    : 'border-white/10 bg-white/5 hover:bg-white/10'
                }`}
                data-testid="theme-option-light"
              >
                <div className="w-full h-14 rounded-lg flex items-center justify-center border border-pink-300/40" style={{ background: 'linear-gradient(180deg, #fff1f7 0%, #fce7f3 45%, #f5d0fe 100%)' }}>
                  <Sun className="w-6 h-6 text-pink-500" />
                </div>
                <span className="font-medium text-xs text-center">{t('settings.lightMode')}</span>
                {theme === 'light' && <span className="text-[10px] text-pink-400">{t('settings.active')}</span>}
              </button>
              <button
                onClick={() => setTheme('white')}
                className={`p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                  theme === 'white' 
                    ? 'border-violet-400 bg-white/20' 
                    : 'border-white/10 bg-white/5 hover:bg-white/10'
                }`}
                data-testid="theme-option-white"
              >
                <div className="w-full h-14 rounded-lg flex items-center justify-center border border-gray-300" style={{ background: 'linear-gradient(180deg, #ffffff 0%, #f7f7fa 100%)' }}>
                  <Sun className="w-6 h-6 text-violet-600" />
                </div>
                <span className="font-medium text-xs text-center">{t('settings.whiteMode') || 'Clair — Blanc'}</span>
                {theme === 'white' && <span className="text-[10px] text-violet-400">{t('settings.active')}</span>}
              </button>
            </div>
          </div>

          {/* Change Password */}
          <div className="bg-white/5 rounded-2xl border border-white/10 p-6">
            <div className="flex items-center space-x-3 mb-4">
              <Lock className="w-6 h-6 text-orange-400" />
              <h2 className="text-xl font-semibold">{t('settings.changePassword')}</h2>
            </div>
            <button 
              onClick={() => setShowChangePasswordModal(true)}
              className="w-full text-left p-3 bg-white/5 hover:bg-white/10 rounded-xl transition-colors flex items-center justify-between"
            >
              <div>
                <div className="font-medium mb-1">{t('settings.changePassword')}</div>
                <div className="text-sm text-gray-400">{t('settings.changePasswordSub')}</div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          {/* Connected Devices */}
          <div className="bg-white/5 rounded-2xl border border-white/10 p-6" data-testid="devices-section">
            <ConnectedDevicesPanel />
          </div>

          {/* Subscription management (v50.15) — hidden while the admin master toggle is OFF */}
          <div data-testid="settings-subscription-section">
            <SubscriptionButton onOpen={() => setShowSubModal(true)} />
            {showSubModal && <SubscriptionModal onClose={() => setShowSubModal(false)} />}
          </div>

          {/* Linked Accounts (multi-account switching) */}
          <div className="bg-white/5 rounded-2xl border border-white/10 p-6" data-testid="linked-accounts-section">
            <LinkedAccountsPanel />
          </div>

          {/* Promote profile section */}
          <div className="bg-white/5 rounded-2xl border border-white/10 p-6" data-testid="promote-profile-section">
            <PromoteProfileSection />
          </div>

          {/* Referral section — invite friends via native share sheet */}
          <ReferralSection />

          {/* Call usage (30 days) */}
          {callUsage && (
            <div className="bg-white/5 rounded-2xl border border-white/10 p-6" data-testid="call-usage-panel">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <BarChart3 className="w-6 h-6 text-fuchsia-400" />
                  <h2 className="text-xl font-semibold">{t('callUsage.title', 'Temps d\'appel')}</h2>
                </div>
                <span className="text-xs text-gray-400">{t('callUsage.last30', '30 derniers jours')}</span>
              </div>
              <p className="text-sm text-gray-400 mb-3">
                {t('callUsage.description', 'Total que vous avez utilisé pour passer des appels (le récepteur n\'est pas décompté).')}
              </p>
              <div className="flex items-end justify-between mb-2">
                <div>
                  <div className="text-2xl font-bold text-fuchsia-300" data-testid="call-usage-total">
                    {formatSeconds(callUsage.total_seconds || 0)}
                  </div>
                  <div className="text-xs text-gray-400">
                    {callUsage.total_calls || 0} {t('callUsage.callsInitiated', 'appel(s) initié(s)').replace(/^\d+\s*/, '')}
                  </div>
                </div>
                <div className="text-xs text-gray-400 text-right">
                  {t('callUsage.quota', 'Quota : 30 min/jour')}
                </div>
              </div>
              <CallUsageChart
                data={(callUsage.series || []).map(d => ({
                  label: d.date.slice(5), // MM-DD
                  value: d.seconds,
                  sublabel: `${d.calls_count} ${t('callUsage.callsShort', 'appel(s)')}`,
                }))}
                height={120}
                barColor="fill-fuchsia-500"
                testid="user-call-usage-chart"
              />
            </div>
          )}

          {/* Refunds — MyMouv cancelled events */}
          <div className="bg-white/5 rounded-2xl border border-white/10 p-6" data-testid="settings-refunds-section">
            <div className="flex items-center space-x-3 mb-2">
              <RefreshCw className="w-6 h-6 text-fuchsia-400" />
              <h2 className="text-xl font-semibold">{t('refunds.sectionTitle', 'Remboursements')}</h2>
            </div>
            <p className="text-sm text-gray-400 mb-4">
              {t('refunds.sectionDescription', 'Vos billets MyMouv annulés — vous pouvez demander un remboursement instantané par carte.')}
            </p>
            <MyRefundsPanel embedded />
          </div>

          {/* My eSIMs */}
          <div className="bg-white/5 rounded-2xl border border-white/10 p-6" data-testid="settings-esims-section">
            <div className="flex items-center space-x-3 mb-2">
              <Wifi className="w-6 h-6 text-purple-400" />
              <h2 className="text-xl font-semibold">{t('esim.mySectionTitle')}</h2>
            </div>
            <MyEsimsPanel embedded />
          </div>

          {/* Privacy */}
          <div className="bg-white/5 rounded-2xl border border-white/10 p-6">
            <div className="flex items-center space-x-3 mb-4">
              <Shield className="w-6 h-6 text-green-400" />
              <h2 className="text-xl font-semibold">{t('settings.privacy')}</h2>
            </div>
            <div className="space-y-3">
              {/* Do Not Disturb (voice calls) */}
              <button
                onClick={toggleDnd}
                className="w-full text-left p-3 bg-white/5 hover:bg-white/10 rounded-xl transition-colors"
                data-testid="toggle-dnd"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-violet-400" />
                    <div>
                      <div className="font-medium mb-1">{t('calls.dnd_title') || 'Ne pas déranger'}</div>
                      <div className="text-sm text-gray-400">
                        {t('calls.dnd_desc') || 'Lorsqu\'il est activé, vous ne recevez plus d\'appels vocaux dans l\'app.'}
                      </div>
                    </div>
                  </div>
                  <div className={`w-12 h-7 rounded-full transition-colors flex items-center px-1 ${dndEnabled ? 'bg-violet-500' : 'bg-gray-600'}`}>
                    <div className={`w-5 h-5 bg-white rounded-full transition-transform ${dndEnabled ? 'translate-x-5' : 'translate-x-0'}`} />
                  </div>
                </div>
              </button>

              <button 
                onClick={async () => {
                  try {
                    // Default is visible (opt-out). Treat missing/null as true.
                    const current = user?.show_phone_in_chat !== false;
                    await axios.patch('/api/users/me', { show_phone_in_chat: !current });
                    refreshUser();
                    toast.success(!current ? 'Numéro visible dans les discussions' : 'Numéro masqué dans les discussions');
                  } catch (e) {
                    toast.error('Erreur lors de la mise à jour');
                  }
                }}
                className="w-full text-left p-3 bg-white/5 hover:bg-white/10 rounded-xl transition-colors"
                data-testid="toggle-phone-visibility"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-green-400" />
                    <div>
                      <div className="font-medium mb-1">{t('settings.phoneInChats')}</div>
                      <div className="text-sm text-gray-400">
                        {user?.show_phone_in_chat !== false
                          ? t('settings.phoneInChatsShown')
                          : t('settings.phoneInChatsHidden')}
                      </div>
                    </div>
                  </div>
                  <div className={`w-12 h-7 rounded-full transition-colors flex items-center px-1 ${user?.show_phone_in_chat !== false ? 'bg-green-500' : 'bg-gray-600'}`}>
                    <div className={`w-5 h-5 bg-white rounded-full transition-transform ${user?.show_phone_in_chat !== false ? 'translate-x-5' : 'translate-x-0'}`} />
                  </div>
                </div>
              </button>

              {/* v60.5 — Stories privées (abonnés uniquement) */}
              <button
                onClick={async () => {
                  try {
                    const next = !user?.stories_private;
                    await axios.patch('/api/users/me', { stories_private: next });
                    refreshUser();
                    toast.success(next ? (PRIVATE_STORIES_I18N[language] || PRIVATE_STORIES_I18N.en).on : (PRIVATE_STORIES_I18N[language] || PRIVATE_STORIES_I18N.en).off);
                  } catch (e) { toast.error('Erreur'); }
                }}
                className="w-full text-left p-3 bg-white/5 hover:bg-white/10 rounded-xl transition-colors"
                data-testid="toggle-private-stories"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Lock className="w-5 h-5 text-fuchsia-400" />
                    <div>
                      <div className="font-medium mb-1">{(PRIVATE_STORIES_I18N[language] || PRIVATE_STORIES_I18N.en).title}</div>
                      <div className="text-sm text-gray-400">{(PRIVATE_STORIES_I18N[language] || PRIVATE_STORIES_I18N.en).sub}</div>
                    </div>
                  </div>
                  <div className={`w-12 h-7 rounded-full transition-colors flex items-center px-1 ${user?.stories_private ? 'bg-fuchsia-500' : 'bg-gray-600'}`}>
                    <div className={`w-5 h-5 bg-white rounded-full transition-transform ${user?.stories_private ? 'translate-x-5' : 'translate-x-0'}`} />
                  </div>
                </div>
              </button>

              {/* Location permission */}
              <button 
                onClick={async () => {
                  if (locationStatus === 'denied') {
                    handleOpenLocationSettings();
                    return;
                  }
                  if (locationStatus === 'prompt' || locationStatus === 'unknown') {
                    try {
                      // Use Capacitor Geolocation plugin which respects the
                      // NSLocationWhenInUseUsageDescription text from Info.plist
                      // (in French) instead of the generic WebView prompt.
                      const { Geolocation } = await import('@capacitor/geolocation');
                      const perm = await Geolocation.requestPermissions();
                      if (perm.location === 'granted' || perm.coarseLocation === 'granted') {
                        setLocationStatus('granted');
                        toast.success(t('settings.locationActivated'));
                      } else {
                        setLocationStatus('denied');
                        toast.error('Permission refusée');
                      }
                    } catch (_) {
                      // Web fallback (browser dev preview)
                      navigator.geolocation.getCurrentPosition(
                        () => { setLocationStatus('granted'); toast.success(t('settings.locationActivated')); },
                        (err) => {
                          if (err.code === 1) { setLocationStatus('denied'); toast.error('Permission refusée'); }
                        }
                      );
                    }
                  }
                }}
                disabled={locationStatus === 'granted'}
                className="w-full text-left p-3 bg-white/5 hover:bg-white/10 rounded-xl transition-colors disabled:opacity-60"
                data-testid="location-permission-btn"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <MapPin className="w-5 h-5 text-emerald-400" />
                    <div>
                      <div className="font-medium mb-1">{t('settings.location')}</div>
                      <div className="text-sm text-gray-400">
                        {locationStatus === 'granted' 
                          ? t('settings.locationOn')
                          : locationStatus === 'denied'
                          ? t('settings.locationOff')
                          : t('settings.locationPromptHint', 'Appuyez pour autoriser le partage de position (discussions et MyMap)')
                        }
                      </div>
                    </div>
                  </div>
                  {locationStatus === 'granted' ? (
                    <div className="w-12 h-7 rounded-full bg-green-500 flex items-center px-1">
                      <div className="w-5 h-5 bg-white rounded-full translate-x-5" />
                    </div>
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </button>
            </div>
          </div>

          {/* My Comments */}
          <div className="bg-white/5 rounded-2xl border border-white/10 p-6">
            <div className="flex items-center space-x-3 mb-4">
              <MessageCircle className="w-6 h-6 text-violet-400" />
              <h2 className="text-xl font-semibold">{t('settings.myComments')}</h2>
            </div>
            <div className="space-y-3">
              <button 
                onClick={() => navigate('/settings/my-comments')}
                className="w-full text-left p-3 bg-white/5 hover:bg-white/10 rounded-xl transition-colors"
              >
                <div className="font-medium mb-1">{t('settings.viewMyComments')}</div>
                <div className="text-sm text-gray-400">{t('settings.viewMyCommentsSub')}</div>
              </button>
            </div>
          </div>

          {/* Statistics */}
          <div className="bg-white/5 rounded-2xl border border-white/10 p-6">
            <div className="flex items-center space-x-3 mb-4">
              <BarChart3 className="w-6 h-6 text-magenta-400" />
              <h2 className="text-xl font-semibold">{t('settings.statisticsTitle') || 'Statistiques et monétisation'}</h2>
            </div>
            <div className="space-y-3">
              <button
                type="button"
                onClick={() => navigate('/settings/statistics')}
                className="w-full text-left p-3 bg-white/5 hover:bg-white/10 rounded-xl transition-colors flex items-center justify-between"
                data-testid="open-statistics-btn"
              >
                <div>
                  <div className="font-medium mb-1">{t('settings.statisticsOpen') || 'Voir mes statistiques et la monétisation'}</div>
                  <div className="text-sm text-gray-400">{t('settings.statisticsSub') || 'Likes, commentaires, partages, lectures, abonnés, sponsors, IBAN'}</div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </button>
            </div>
          </div>

          {/* Notifications */}
          <div className="bg-white/5 rounded-2xl border border-white/10 p-6">
            <div className="flex items-center space-x-3 mb-4">
              <Bell className="w-6 h-6 text-yellow-400" />
              <h2 className="text-xl font-semibold">{t('settings.notifications')}</h2>
            </div>
            <div className="space-y-3">
              <button 
                onClick={requestNotificationPermission}
                disabled={requestingNotifications}
                className="w-full text-left p-3 bg-white/5 hover:bg-white/10 rounded-xl transition-colors disabled:opacity-50"
                data-testid="notifications-toggle-btn"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium mb-1">{t('settings.pushNotifications')}</div>
                    <div className="text-sm text-gray-400">
                      {notificationStatus === 'granted' 
                        ? t('settings.notifStatusGranted', '✅ Activées — appuyez pour gérer dans Réglages iPhone')
                        : notificationStatus === 'denied'
                        ? t('settings.notifStatusDenied', '❌ Désactivées — appuyez pour ouvrir les Réglages iPhone')
                        : notificationStatus === 'web'
                        ? t('settings.notifStatusWeb', "Disponible sur l'app mobile uniquement")
                        : t('settings.notifStatusEnable', 'Appuyez pour activer les notifications')
                      }
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </div>
              </button>

              {/* Weekly cinema digest opt-in */}
              <div className="w-full flex items-center justify-between p-3 bg-white/5 rounded-xl" data-testid="weekly-cinema-row">
                <div className="flex-1 min-w-0 pr-3">
                  <div className="font-medium mb-1">{t('settings.weeklyCinemaDigest', 'Prochaines sorties ciné (hebdo)')}</div>
                  <div className="text-sm text-gray-400">{t('settings.weeklyCinemaDigestSub', 'Recevez chaque semaine une notification + un email avec les nouvelles sorties.')}</div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                  <input
                    type="checkbox"
                    checked={weeklyCinemaEnabled}
                    onChange={async (e) => {
                      const next = e.target.checked;
                      setWeeklyCinemaEnabled(next);
                      try {
                        await axios.put('/api/users/me/notification-prefs', { weekly_cinema_digest: next });
                      } catch (_) {
                        setWeeklyCinemaEnabled(!next);
                        toast.error(t('settings.errorSaving', 'Erreur, veuillez réessayer'));
                      }
                    }}
                    className="sr-only peer"
                    data-testid="weekly-cinema-checkbox"
                  />
                  <div className="w-11 h-6 bg-white/10 rounded-full peer peer-checked:bg-magenta-500 relative">
                    <div className="absolute top-0.5 left-0.5 bg-white w-5 h-5 rounded-full transition-transform peer-checked:translate-x-5"></div>
                  </div>
                </label>
              </div>
            </div>
          </div>

          {/* Réclamations */}
          <div className="bg-white/5 rounded-2xl border border-white/10 p-6">
            <div className="flex items-center space-x-3 mb-4">
              <FileText className="w-6 h-6 text-orange-400" />
              <h2 className="text-xl font-semibold">{t('settings.reportsTitle')}</h2>
            </div>
            <div className="space-y-3">
              <a 
                href="https://info.destine.studio/document"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full text-left p-3 bg-white/5 hover:bg-white/10 rounded-xl transition-colors block"
              >
                <div className="font-medium mb-1">{t('settings.makeReport')}</div>
                <div className="text-sm text-gray-400">{t('settings.makeReportSub')}</div>
              </a>
            </div>
          </div>

          {/* Blocked Users */}
          <div className="bg-white/5 rounded-2xl border border-white/10 p-6">
            <div className="flex items-center space-x-3 mb-4">
              <UserX className="w-6 h-6 text-red-400" />
              <h2 className="text-xl font-semibold">{t('settings.blockedUsers')}</h2>
            </div>
            <p className="text-sm text-gray-400 mb-4">
              {t('settings.blockedUsersDesc')}
            </p>
            
            {/* Search to block */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('settings.searchUserToBlock')}
                className="w-full pl-10 pr-10 py-3 bg-white/10 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-red-500"
              />
              {searchQuery && (
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setSearchResults([]);
                  }}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>

            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="space-y-2 mb-4 max-h-48 overflow-y-auto">
                {searchResults.map(searchUser => (
                  <div 
                    key={searchUser.id}
                    className="flex items-center justify-between p-3 bg-white/5 rounded-xl"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-magenta-500 to-violet-600 flex items-center justify-center overflow-hidden">
                        {searchUser.profile_image_url ? (
                          <img
                            src={getMediaUrl(searchUser.profile_image_url)}
                            alt={searchUser.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-sm font-bold">
                            {searchUser.name?.charAt(0).toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div>
                        <p className="font-medium">{searchUser.name}</p>
                        {searchUser.username && (
                          <p className="text-xs text-gray-400">@{searchUser.username}</p>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => handleBlockUser(searchUser.id, searchUser.name)}
                      className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg text-sm font-medium transition-colors"
                    >
                      Bloquer
                    </button>
                  </div>
                ))}
              </div>
            )}

            {searching && (
              <div className="text-center text-gray-400 py-2">
                Recherche en cours...
              </div>
            )}

            {/* Blocked Users List */}
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-gray-400 mb-2">{t('settings.blockedUsersCount')} ({blockedUsers.length})</h3>
              
              {loadingBlocked ? (
                <div className="text-center text-gray-400 py-4">Chargement...</div>
              ) : blockedUsers.length === 0 ? (
                <div className="text-center text-gray-500 py-4 text-sm">
                  {t('settings.noBlockedUser')}
                </div>
              ) : (
                blockedUsers.map(blockedUser => (
                  <div 
                    key={blockedUser.id}
                    className="flex items-center justify-between p-3 bg-red-500/10 border border-red-500/20 rounded-xl"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-600 to-gray-700 flex items-center justify-center overflow-hidden">
                        {blockedUser.profile_image_url ? (
                          <img
                            src={getMediaUrl(blockedUser.profile_image_url)}
                            alt={blockedUser.name}
                            className="w-full h-full object-cover opacity-50"
                          />
                        ) : (
                          <span className="text-sm font-bold text-gray-400">
                            {blockedUser.name?.charAt(0).toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-gray-300">{blockedUser.name}</p>
                        {blockedUser.username && (
                          <p className="text-xs text-gray-500">@{blockedUser.username}</p>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => handleUnblockUser(blockedUser.id, blockedUser.name)}
                      className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg text-sm font-medium transition-colors"
                    >
                      Débloquer
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* CGU / Documents juridiques */}
          <a
            href="https://info.destine.studio/document"
            target="_blank"
            rel="noopener noreferrer"
            className="block bg-white/5 rounded-2xl border border-white/10 p-6 hover:bg-white/10 transition-colors"
            data-testid="cgu-link"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <FileText className="w-6 h-6 text-cyan-400" />
                <div>
                  <h2 className="text-xl font-semibold">{t('settings.legalDocs')}</h2>
                  <p className="text-sm text-gray-400 mt-1">{t('settings.legalDocsSub')}</p>
                </div>
              </div>
              <ExternalLink className="w-5 h-5 text-gray-400" />
            </div>
          </a>

          {/* À propos de Destine IA */}
          <div className="bg-gradient-to-br from-magenta-500/10 to-violet-600/10 rounded-2xl border border-magenta-500/20 p-6" data-testid="about-ia-section">
            <div className="flex items-center space-x-3 mb-4">
              <Film className="w-6 h-6 text-magenta-400" />
              <h2 className="text-xl font-semibold">{t('settings.aboutIATitle')}</h2>
            </div>
            <div className="space-y-3 text-sm text-gray-300 leading-relaxed">
              <p>
                {t('settings.aboutIAIntro')}
              </p>
              <div className="bg-white/5 rounded-xl p-4 space-y-2">
                <p className="font-semibold text-magenta-300">{t('settings.aboutIACanDo')}</p>
                <ul className="space-y-1.5 list-disc list-inside text-gray-300">
                  {t('settings.aboutIAFeatures').split(';').map((f, i) => (
                    <li key={i}>{f}</li>
                  ))}
                </ul>
              </div>
              <div className="bg-white/5 rounded-xl p-4 space-y-2">
                <p className="font-semibold text-cyan-300">{t('settings.aboutIAPrivacy')}</p>
                <ul className="space-y-1.5 list-disc list-inside text-gray-300">
                  {t('settings.aboutIAPrivacyPoints').split(';').map((f, i) => (
                    <li key={i}>{f}</li>
                  ))}
                </ul>
              </div>
              <button
                onClick={() => navigate('/film-ia')}
                className="w-full mt-2 py-3 bg-gradient-to-r from-magenta-500 to-violet-600 rounded-xl font-semibold hover:from-magenta-600 hover:to-violet-700 transition-all"
                data-testid="open-film-ia-btn"
              >
                {t('settings.openIA')}
              </button>
            </div>
          </div>

          {/* Country preference (Where to watch) */}
          <CountrySelector />

          {/* Security */}
          <div className="bg-white/5 rounded-2xl border border-white/10 p-6">
            <div className="flex items-center space-x-3 mb-4">
              <Lock className="w-6 h-6 text-purple-400" />
              <h2 className="text-xl font-semibold">{t('settings.security')}</h2>
            </div>
            <div className="space-y-3">
              <button 
                onClick={() => navigate('/')} 
                className="w-full text-left p-3 bg-white/5 hover:bg-white/10 rounded-xl transition-colors"
              >
                <div className="font-medium mb-1">Changer le mot de passe</div>
                <div className="text-sm text-gray-400">Utilisez &quot;Mot de passe oublié&quot; sur la page de connexion</div>
              </button>
            </div>
          </div>

          {/* Google Play AI policy — report AI-generated content from Settings (v50.10.8) */}
          <button
            onClick={() => setShowAIReport(true)}
            className="w-full p-4 rounded-2xl bg-white/5 border border-orange-400/30 hover:bg-orange-500/10 transition-colors flex items-center justify-between"
            data-testid="settings-report-ai-btn"
          >
            <span className="flex items-center gap-3 text-left">
              <Flag className="w-5 h-5 text-orange-400 shrink-0" />
              <span className="font-semibold">{aiReportTexts(language).title}</span>
            </span>
            <ChevronRight className="w-5 h-5 text-gray-400" />
          </button>
          {showAIReport && <ReportAIContentModal feature="settings" onClose={() => setShowAIReport(false)} />}

          {/* Inactivity notice removed (v60.3) */}

          {/* Logout */}
          <button
            onClick={handleLogout}
            className="w-full p-4 bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 rounded-2xl font-semibold transition-all flex items-center justify-center space-x-2"
            data-testid="settings-logout-btn"
          >
            <LogOut className="w-5 h-5" />
            <span>{t('settings.logout')}</span>
          </button>

          {/* Delete Account */}
          <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-6">
            <div className="flex items-center space-x-3 mb-4">
              <Trash2 className="w-6 h-6 text-red-400" />
              <h2 className="text-xl font-semibold text-red-400">{t('settings.dangerZone')}</h2>
            </div>
            {!showDeleteConfirm ? (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="w-full p-3 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-xl transition-colors font-medium"
              >
                {t('settings.deleteAccount')}
              </button>
            ) : (
              <div className="space-y-3" data-testid="delete-confirm-section">
                <p className="text-sm text-gray-300">
                  Êtes-vous sûr ? Cette action est <strong>irréversible</strong>.
                </p>
                <p className="text-xs text-gray-400">
                  Pour confirmer, saisissez la date de naissance que vous avez utilisée lors de l&apos;inscription :
                </p>
                <input
                  type="date"
                  value={confirmDob}
                  onChange={(e) => setConfirmDob(e.target.value)}
                  className="w-full px-3 py-2 bg-white/10 border border-red-500/30 rounded-xl text-white focus:outline-none focus:border-red-500"
                  data-testid="confirm-dob-input"
                />
                <div className="flex space-x-2">
                  <button
                    onClick={() => { setShowDeleteConfirm(false); setConfirmDob(''); }}
                    disabled={deleting}
                    className="flex-1 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-xl transition-colors disabled:opacity-50"
                    data-testid="cancel-delete-btn"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleDeleteAccount}
                    disabled={deleting || !confirmDob}
                    className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-600 rounded-xl transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="confirm-delete-btn"
                  >
                    {deleting ? 'Suppression…' : 'Confirmer la suppression'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Language Selection Modal */}
      {showLanguageModal && (
        <LanguageModal
          currentLanguage={language}
          onSelect={(lang) => {
            setLanguage(lang);
            setShowLanguageModal(false);
            toast.success(t('settings.language') + ': ' + getLanguageName(lang));
          }}
          onClose={() => setShowLanguageModal(false)}
          supportedLanguages={supportedLanguages}
          getLanguageName={getLanguageName}
          getFlag={getFlag}
          t={t}
        />
      )}

      {/* Change Password Modal */}
      {showChangePasswordModal && (
        <ChangePasswordModal
          user={user}
          onClose={() => setShowChangePasswordModal(false)}
          onSuccess={() => {
            setShowChangePasswordModal(false);
            refreshUser && refreshUser();
            toast.success('Informations mises à jour');
          }}
          t={t}
        />
      )}

      {showChangeEmailModal && (
        <ChangeEmailModal
          currentEmail={user?.email}
          onClose={() => setShowChangeEmailModal(false)}
          onSuccess={() => {
            setShowChangeEmailModal(false);
            refreshUser && refreshUser();
          }}
        />
      )}

      <Navigation />
    </div>
  );
};

// Language Selection Modal
const LanguageModal = ({ currentLanguage, onSelect, onClose, supportedLanguages, getLanguageName, getFlag, t }) => {
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-gray-900 border border-white/10 rounded-3xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold">{t('settings.selectLanguage')}</h2>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-2 max-h-[60vh] overflow-y-auto overscroll-contain" onTouchMove={(e) => e.stopPropagation()}>
          {supportedLanguages.map((lang) => (
            <button
              key={lang}
              onClick={() => onSelect(lang)}
              className={`w-full p-4 rounded-xl flex items-center justify-between transition-colors ${
                currentLanguage === lang
                  ? 'bg-cyan-500/20 border border-cyan-500/50'
                  : 'bg-white/5 hover:bg-white/10 border border-transparent'
              }`}
            >
              <div className="flex items-center gap-3">
                <span className="text-2xl">{getFlag(lang)}</span>
                <span className="font-medium">{getLanguageName(lang)}</span>
              </div>
              {currentLanguage === lang && (
                <div className="w-6 h-6 bg-cyan-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm">✓</span>
                </div>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// Change Password Modal — new secure 3-step flow (DOB → email OTP → new password)
const ChangePasswordModal = ({ user, onClose, onSuccess, t }) => {
  // step: 'dob' | 'code' | 'new'
  const [step, setStep] = useState('dob');
  const [dob, setDob] = useState('');
  const [siret, setSiret] = useState('');
  const [code, setCode] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const isPro = user?.account_type === 'PRO';

  const submitDob = async (e) => {
    e.preventDefault();
    if (!dob) { toast.error(t('resetPwd.dobRequired') || 'Date de naissance obligatoire'); return; }
    setLoading(true);
    try {
      await axios.post('/api/auth/forgot-password', {
        email: user.email,
        date_of_birth: dob,
        siret: isPro ? siret : undefined,
      });
      toast.success(t('resetPwd.codeSent') || 'Code envoyé par email');
      setStep('code');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) { toast.error(t('resetPwd.codeFormat') || 'Code à 6 chiffres'); return; }
    setLoading(true);
    try {
      const r = await axios.post('/api/auth/verify-reset-code', { email: user.email, code });
      setResetToken(r.data?.reset_token);
      setStep('new');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Code incorrect');
    } finally {
      setLoading(false);
    }
  };

  const submitNewPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) { toast.error(t('auth.passwordTooShort') || 'Mot de passe trop court'); return; }
    if (newPassword !== confirmPassword) { toast.error(t('auth.passwordMismatch') || 'Les mots de passe ne correspondent pas'); return; }
    setLoading(true);
    try {
      await axios.post('/api/auth/reset-password', { reset_token: resetToken, new_password: newPassword });
      toast.success(t('resetPwd.updated') || 'Mot de passe mis à jour');
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4" data-testid="change-password-modal">
      <div className="bg-gray-900 border border-white/10 rounded-3xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold">{t('settings.changePassword')}</h2>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>

        {step === 'dob' && (
          <form onSubmit={submitDob} className="space-y-4">
            <p className="text-sm text-gray-400">{t('resetPwd.dobIntro') || 'Confirme ta date de naissance pour recevoir un code de vérification par email.'}</p>
            <div>
              <label className="block text-sm font-medium mb-2">{t('auth.birthDate') || 'Date de naissance'}</label>
              <input
                data-testid="reset-dob-input"
                type="date"
                value={dob}
                onChange={(e) => setDob(e.target.value)}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:border-orange-500 text-white"
                required
              />
            </div>
            {isPro && (
              <div>
                <label className="block text-sm font-medium mb-2">{t('auth.siret')}</label>
                <input
                  type="text"
                  value={siret}
                  onChange={(e) => setSiret(e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:border-orange-500"
                />
              </div>
            )}
            <button
              data-testid="reset-dob-submit"
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-orange-500 to-red-600 rounded-full font-semibold disabled:opacity-50"
            >
              {loading ? t('common.loading') : (t('resetPwd.sendCode') || 'Recevoir le code par email')}
            </button>
          </form>
        )}

        {step === 'code' && (
          <form onSubmit={submitCode} className="space-y-4">
            <p className="text-sm text-gray-400">{t('resetPwd.codeIntro') || 'Entre le code à 6 chiffres reçu par email.'}</p>
            <input
              data-testid="reset-code-input"
              type="text"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              placeholder="000000"
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:border-orange-500 text-center text-2xl tracking-widest font-mono"
              required
            />
            <button
              data-testid="reset-code-submit"
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-orange-500 to-red-600 rounded-full font-semibold disabled:opacity-50"
            >
              {loading ? t('common.loading') : (t('resetPwd.verifyCode') || 'Vérifier')}
            </button>
            <button
              type="button"
              onClick={() => setStep('dob')}
              className="w-full py-2 text-sm text-gray-400 hover:text-white"
            >
              {t('resetPwd.resend') || 'Renvoyer un code'}
            </button>
          </form>
        )}

        {step === 'new' && (
          <form onSubmit={submitNewPassword} className="space-y-4">
            <p className="text-sm text-emerald-300">{t('resetPwd.codeVerified') || 'Code vérifié. Choisis ton nouveau mot de passe.'}</p>
            <div>
              <label className="block text-sm font-medium mb-2">{t('auth.newPassword')}</label>
              <input
                data-testid="reset-newpwd-input"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:border-orange-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">{t('auth.confirmPassword')}</label>
              <input
                data-testid="reset-confirmpwd-input"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:border-orange-500"
                required
              />
            </div>
            <button
              data-testid="reset-newpwd-submit"
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-full font-semibold disabled:opacity-50"
            >
              {loading ? t('common.loading') : (t('resetPwd.confirm') || 'Confirmer le nouveau mot de passe')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

const ChangeEmailModal = ({ onClose, onSuccess, currentEmail }) => {
  const { t } = useLanguage();
  const [newEmail, setNewEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newEmail.trim() || !password) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    if (newEmail.trim().toLowerCase() === currentEmail?.toLowerCase()) {
      toast.error("C'est déjà votre adresse email actuelle");
      return;
    }
    setLoading(true);
    try {
      await axios.post('/api/users/me/change-email', {
        new_email: newEmail.trim(),
        password
      });
      toast.success('Adresse email modifiée avec succès');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la modification');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-start justify-center overflow-y-auto pt-8 pb-4 px-4">
      <div className="bg-gray-900 border border-white/10 rounded-3xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold">{t('changeEmail.title', "Modifier l'adresse email")}</h2>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-sm text-gray-400 mb-4">
          {t('changeEmail.description', 'Votre nouvelle adresse email sera utilisée pour vos prochaines connexions.')}
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">{t('changeEmail.currentLabel', 'Adresse email actuelle')}</label>
            <div className="px-4 py-3 bg-white/5 border border-white/5 rounded-xl text-gray-500">
              {currentEmail}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">{t('changeEmail.newEmailLabel', 'Nouvelle adresse email')}</label>
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="nouvelle@email.com"
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:border-blue-500"
              data-testid="new-email-input"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">{t('changeEmail.passwordLabel', 'Mot de passe actuel')}</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('changeEmail.passwordPlaceholder', 'Confirmez votre mot de passe')}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:border-blue-500"
              data-testid="email-change-password"
              required
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-white/5 hover:bg-white/10 rounded-full font-semibold"
            >
              {t('changeEmail.cancel', 'Annuler')}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 rounded-full font-semibold disabled:opacity-50"
              data-testid="submit-email-change"
            >
              {loading ? t('changeEmail.inProgress', 'En cours...') : t('changeEmail.submit', 'Modifier')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Settings;

// Country preference selector for "Where to watch"
const CountrySelector = () => {
  const { t } = useLanguage();
  const { refreshUser } = useAuth();
  const [country, setCountry] = React.useState('FR');
  const [saving, setSaving] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);

  React.useEffect(() => {
    axios.get('/api/auth/me')
      .then(res => {
        if (res.data?.preferred_country) setCountry(res.data.preferred_country);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, []);

  const COUNTRIES = [
    ['FR', '🇫🇷 France'], ['BE', '🇧🇪 Belgique'], ['CH', '🇨🇭 Suisse'], ['CA', '🇨🇦 Canada'],
    ['US', '🇺🇸 États-Unis'], ['GB', '🇬🇧 Royaume-Uni'], ['DE', '🇩🇪 Allemagne'],
    ['ES', '🇪🇸 Espagne'], ['IT', '🇮🇹 Italie'], ['PT', '🇵🇹 Portugal'],
    ['NL', '🇳🇱 Pays-Bas'], ['MA', '🇲🇦 Maroc'], ['DZ', '🇩🇿 Algérie'],
    ['TN', '🇹🇳 Tunisie'], ['SN', '🇸🇳 Sénégal'], ['CI', '🇨🇮 Côte d\'Ivoire'],
    ['CM', '🇨🇲 Cameroun'], ['HT', '🇭🇹 Haïti'], ['BR', '🇧🇷 Brésil'],
    ['MX', '🇲🇽 Mexique'], ['AR', '🇦🇷 Argentine'], ['JP', '🇯🇵 Japon'],
    ['CN', '🇨🇳 Chine'], ['IN', '🇮🇳 Inde'], ['AU', '🇦🇺 Australie'],
  ];

  const save = async (code) => {
    setSaving(true);
    try {
      await axios.post('/api/user/country', { country_code: code });
      setCountry(code);
      // Refresh the AuthContext so Explorer "Where to watch" picks up the new value
      try { await refreshUser?.(); } catch (_e) { /* ignore */ }
      toast.success(t('settings.updatedToast'));
    } catch (e) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white/5 rounded-2xl border border-white/10 p-6" data-testid="country-selector-section">
      <div className="flex items-center space-x-3 mb-3">
        <span className="text-2xl">🌍</span>
        <div className="flex-1">
          <h2 className="text-xl font-semibold">{t('settings.countryForWatch')}</h2>
          <p className="text-sm text-gray-400 mt-1">{t('settings.countryForWatchSubtitle')}</p>
        </div>
      </div>
      <select
        value={country}
        onChange={(e) => save(e.target.value)}
        disabled={!loaded || saving}
        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-magenta-500 disabled:opacity-50"
        data-testid="country-select"
      >
        {COUNTRIES.map(([code, label]) => (
          <option key={code} value={code} className="bg-gray-900">{label}</option>
        ))}
      </select>
    </div>
  );
};

